import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import {
  apiMessage,
  getScorePolicy,
  listScorePolicies,
  saveScorePolicy,
} from '../services/jobAuditApi';

const fieldClass =
  'mt-1 block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm shadow-sm disabled:bg-gray-50 disabled:text-gray-700';
const numClass =
  'mt-1 block w-24 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm shadow-sm disabled:bg-gray-50 disabled:text-gray-700';

const matcherFields = (spec) => {
  if (!spec) return [];
  const ids = [...(spec.field_ids || [])];
  (spec.matchers || []).forEach((child) => {
    ids.push(...matcherFields(child));
  });
  return ids;
};

const JobAuditScoringAdmin = () => {
  const { isAdmin } = useAuth();
  const canEdit = isAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listScorePolicies()
      .then((listing) => {
        const first = (listing.items || [])[0];
        if (!first) return null;
        return getScorePolicy(first.policy_id, first.policy_version);
      })
      .then((payload) => {
        if (!cancelled) setPolicy(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(apiMessage(err, 'Failed to load score policy'));
          setPolicy(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const totalWeight = useMemo(
    () => (policy?.items || []).reduce((sum, item) => sum + Number(item.weight || 0), 0),
    [policy]
  );

  const patchPolicy = (partial) => {
    if (!canEdit) return;
    setPolicy((current) => (current ? { ...current, ...partial } : current));
  };

  const patchBands = (partial) => {
    if (!canEdit) return;
    setPolicy((current) =>
      current ? { ...current, bands: { ...current.bands, ...partial } } : current
    );
  };

  const patchItem = (itemId, partial) => {
    if (!canEdit) return;
    setPolicy((current) => {
      if (!current) return current;
      return {
        ...current,
        items: current.items.map((item) =>
          item.id === itemId ? { ...item, ...partial } : item
        ),
      };
    });
  };

  const patchCap = (capId, partial) => {
    if (!canEdit) return;
    setPolicy((current) => {
      if (!current) return current;
      return {
        ...current,
        caps: (current.caps || []).map((cap) =>
          cap.id === capId ? { ...cap, ...partial } : cap
        ),
      };
    });
  };

  const onSave = async () => {
    if (!canEdit || !policy) return;
    setSaving(true);
    try {
      const saved = await saveScorePolicy(policy.policy_id, policy);
      setPolicy(saved);
      toast.success('Score policy saved');
    } catch (err) {
      toast.error(apiMessage(err, 'Failed to save score policy'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-600">Loading score policy…</div>;
  }
  if (!policy) {
    return <div className="text-sm text-gray-600">No score policy is configured.</div>;
  }

  const lowMax = Number(policy.bands?.low_max ?? 49);
  const mediumMax = Number(policy.bands?.medium_max ?? 74);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Scoring Admin</h1>
          <p className="mt-1 text-sm text-gray-600">
            {policy.form_name} · {policy.form_id} {policy.form_version} · v{policy.policy_version}
          </p>
          {!canEdit && (
            <p className="mt-1 text-sm text-gray-500">View only. Administrators can edit weights and bands.</p>
          )}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-3 rounded-lg bg-white p-4 text-sm shadow md:grid-cols-4">
        <div>
          <dt className="text-xs uppercase text-gray-500">Status</dt>
          <dd className="mt-1 text-gray-900">{policy.status}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase text-gray-500">Method</dt>
          <dd className="mt-1 text-gray-900">{policy.scoring_method}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase text-gray-500">Items</dt>
          <dd className="mt-1 text-gray-900">{policy.items.length}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase text-gray-500">Total weight</dt>
          <dd className="mt-1 text-gray-900">{totalWeight}</dd>
        </div>
      </dl>

      <div className="rounded-lg bg-white p-4 shadow">
        <h2 className="text-sm font-semibold text-gray-800">Score bands</h2>
        <p className="mt-1 text-sm text-gray-500">
          Low 0–{lowMax} · Medium {lowMax + 1}–{mediumMax} · High {mediumMax + 1}–100
        </p>
        <div className="mt-3 flex flex-wrap gap-4">
          <label className="text-sm text-gray-700">
            Low max
            <input
              type="number"
              min={0}
              max={100}
              disabled={!canEdit}
              className={numClass}
              value={lowMax}
              onChange={(e) => patchBands({ low_max: Number(e.target.value) })}
            />
          </label>
          <label className="text-sm text-gray-700">
            Medium max
            <input
              type="number"
              min={0}
              max={100}
              disabled={!canEdit}
              className={numClass}
              value={mediumMax}
              onChange={(e) => patchBands({ medium_max: Number(e.target.value) })}
            />
          </label>
        </div>
      </div>

      {(policy.caps || []).length > 0 && (
        <div className="rounded-lg bg-white p-4 shadow">
          <h2 className="text-sm font-semibold text-gray-800">Caps</h2>
          <div className="mt-3 space-y-3">
            {policy.caps.map((cap) => (
              <div key={cap.id} className="flex flex-wrap items-end gap-4">
                <label className="min-w-[16rem] flex-1 text-sm text-gray-700">
                  Label
                  <input
                    disabled={!canEdit}
                    className={fieldClass}
                    value={cap.label || ''}
                    onChange={(e) => patchCap(cap.id, { label: e.target.value })}
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Max score
                  <input
                    type="number"
                    min={0}
                    max={100}
                    disabled={!canEdit}
                    className={numClass}
                    value={cap.max_score}
                    onChange={(e) => patchCap(cap.id, { max_score: Number(e.target.value) })}
                  />
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <h2 className="border-b px-4 py-3 text-sm font-semibold text-gray-800">Score items</h2>
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Item</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Weight</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Issue label</th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Pass when</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {policy.items.map((item) => (
              <tr key={item.id} className="align-top">
                <td className="px-4 py-3">
                  <input
                    disabled={!canEdit}
                    className={fieldClass}
                    value={item.label}
                    onChange={(e) => patchItem(item.id, { label: e.target.value })}
                  />
                  <div className="mt-1 font-mono text-xs text-gray-400">
                    {item.section} · {item.id}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    min={1}
                    disabled={!canEdit}
                    className={numClass}
                    value={item.weight}
                    onChange={(e) => patchItem(item.id, { weight: Number(e.target.value) })}
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    disabled={!canEdit}
                    className={fieldClass}
                    value={item.issue_label || ''}
                    onChange={(e) => patchItem(item.id, { issue_label: e.target.value })}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {matcherFields(item.pass_when).map((fieldId) => (
                      <span
                        key={fieldId}
                        className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600"
                      >
                        {fieldId}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobAuditScoringAdmin;
